'use client';

import React, { useMemo } from 'react';
import { Cell, Label, Pie, PieChart, Sector } from 'recharts';
import { PieSectorDataItem } from 'recharts/types/polar/Pie';

import { track } from '@repo/analytics';
import { SNIPPET_TYPE } from '@repo/types';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@repo/ui/components/ui/chart';

import { useResponsiveRadialChartDimensions } from '@/lib/chart-utils';
import { usePlatform } from '@/hooks/use-platform';
import { useAnalysisContainer } from '@/components/article-analysis/analysis-container';

interface ScoresChartProps {
  claimedFactsScores?: number;
  opinionStatementsScores?: number;
  dubiousClaimsScores?: number;
  currTab: SNIPPET_TYPE;
  setCurrTab: (tab: SNIPPET_TYPE) => void;
  /** Article slug (analytics wire id) for `article_snippet_tab_viewed`. */
  articleId?: string;
}

export const ScoresChart: React.FC<ScoresChartProps> = ({
  claimedFactsScores,
  opinionStatementsScores,
  dubiousClaimsScores,
  currTab,
  setCurrTab,
  articleId,
}) => {
  const { isWebkit } = usePlatform();

  const chartDimensions = useResponsiveRadialChartDimensions(
    'large',
    useAnalysisContainer(),
  );

  const dubiMeterChart = {
    chartData: [
      {
        type: 'facts',
        enumType: SNIPPET_TYPE.CLAIMEDFACTS,
        score: Number.isFinite(claimedFactsScores)
          ? Math.round(claimedFactsScores || 0)
          : 0,
        fill: '#00B378',
      },
      {
        type: 'opinions',
        enumType: SNIPPET_TYPE.OPINIONSTATEMENTS,
        score: Number.isFinite(opinionStatementsScores)
          ? Math.round(opinionStatementsScores || 0)
          : 0,
        fill: '#FFD700',
      },
      {
        type: 'dubious',
        enumType: SNIPPET_TYPE.DUBIOUSCLAIMS,
        score: Number.isFinite(dubiousClaimsScores)
          ? Math.round(dubiousClaimsScores || 0)
          : 0,
        fill: '#EA420F',
      },
    ],
    chartConfig: {
      score: {
        label: 'Score',
      },
      facts: {
        label: 'Claimed Facts',
        color: '#00B378',
      },
      opinions: {
        label: 'Opinions',
        color: '#FFD700',
      },
      dubious: {
        // CRE-629: header label softened from 'Dubious Claims' → 'Claims'
        // (defamation-risk reduction). Internal type name DUBIOUSCLAIMS preserved.
        label: 'Claims',
        color: '#EA420F',
      },
    } satisfies ChartConfig,
  };

  const currDubMeterScore = useMemo(() => {
    switch (currTab) {
      case SNIPPET_TYPE.CLAIMEDFACTS:
        return {
          index: 0,
          score: dubiMeterChart.chartData[0]?.score,
          label: 'Facts',
        };
      case SNIPPET_TYPE.OPINIONSTATEMENTS:
        return {
          index: 1,
          score: dubiMeterChart.chartData[1]?.score,
          label: 'Opinions',
        };
      case SNIPPET_TYPE.DUBIOUSCLAIMS:
        return {
          index: 2,
          score: dubiMeterChart.chartData[2]?.score,
          label: 'Claims',
        };
      default:
        return { index: 0, score: 0, label: '' };
    }
  }, [currTab, dubiMeterChart.chartData]);

  const innerRadius = useMemo(() => {
    if (chartDimensions.innerRadius >= 57) {
      return 46;
    }
    if (chartDimensions.innerRadius >= 43) {
      return 36;
    }
    if (chartDimensions.innerRadius >= 40) {
      return 33;
    }
    return 30;
  }, [chartDimensions.innerRadius]);

  const labelsY = useMemo(() => {
    if (chartDimensions.innerRadius >= 57) {
      return {
        top: 10,
        bottom: 13,
      };
    }
    if (chartDimensions.innerRadius >= 43) {
      return {
        top: 8,
        bottom: 11,
      };
    }
    if (chartDimensions.innerRadius >= 40) {
      return {
        top: 6,
        bottom: 10,
      };
    }
    return {
      top: 6,
      bottom: 10,
    };
  }, [chartDimensions.innerRadius]);

  const chartMargins = useMemo(() => {
    let margin = -8;

    if (chartDimensions.innerRadius >= 57) {
      margin = -1.5;
    } else if (chartDimensions.innerRadius >= 43) {
      margin = -5;
    } else if (chartDimensions.innerRadius >= 40) {
      margin = -7;
    }

    return {
      top: margin,
      right: margin,
      bottom: margin,
      left: margin,
    };
  }, [chartDimensions.innerRadius]);

  return (
    <ChartContainer
      config={dubiMeterChart.chartConfig}
      className="aspect-square max-h-20 w-full @cred-md:max-h-22.75 @cred-lg:max-h-24.75 @cred-xl:max-h-32.5"
    >
      <PieChart margin={chartMargins}>
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel
              className="min-w-10 max-w-26 px-2 py-1 @cred-md:max-w-30 @cred-xl:max-w-50"
              formatter={(value, name, item) => (
                <div className="flex items-center gap-1.5 @cred-md:gap-2 @cred-xl:gap-3">
                  <div
                    className="size-2.5 shrink-0 rounded-xs bg-(--color-bg) @cred-md:size-3 @cred-xl:size-4"
                    style={
                      {
                        '--color-bg': item.payload.fill,
                        '--color-border': item.payload.fill,
                      } as React.CSSProperties
                    }
                  />
                  <div className="flex flex-1 shrink-0 items-center justify-between gap-4 leading-none">
                    <div className="grid gap-1.5">
                      <span className="text-[11px] text-muted-foreground @cred-md:text-[13px] @cred-xl:text-base">
                        {dubiMeterChart.chartConfig[
                          name as keyof typeof dubiMeterChart.chartConfig
                        ]?.label || name}
                      </span>
                    </div>
                    {value && (
                      <span className="font-mono text-[11px] font-medium tabular-nums text-foreground @cred-md:text-[13px] @cred-xl:text-base">
                        {value.toLocaleString()}%
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
          }
        />
        <Pie
          isAnimationActive
          data={dubiMeterChart.chartData}
          dataKey="score"
          nameKey="type"
          paddingAngle={1}
          innerRadius={innerRadius}
          startAngle={90}
          endAngle={450}
          cornerRadius={1.5}
          onClick={(_, index) => {
            const enumType = dubiMeterChart.chartData[index]?.enumType;
            if (enumType) {
              setCurrTab(enumType);
              // Typed track() per doctrine §6 — same registered event the
              // iframe fires for this interaction (was the unregistered raw
              // 'select_snippets_tab', a CRE-793 cross-surface split).
              track('article_snippet_tab_viewed', {
                article_id: articleId ?? '',
                tab: enumType,
              });
            }
          }}
          activeIndex={currDubMeterScore.index}
          activeShape={({
            innerRadius = 0,
            outerRadius = 0,
            ...props
          }: PieSectorDataItem) => (
            <Sector
              {...props}
              innerRadius={innerRadius - 1.5}
              outerRadius={outerRadius + 1}
            />
          )}
          className="focus:outline-hidden"
        >
          {dubiMeterChart.chartData.map((entry) => (
            <Cell
              key={entry.enumType}
              id={entry.enumType}
              fill={entry.fill}
              style={{ outline: 'none' }}
              className="origin-center scale-100 outline-hidden transition-transform duration-200 ease-out hover:scale-[1.038] focus:outline-hidden"
            />
          ))}
          <Label
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) - 1
                          : (viewBox.cy || 0) - labelsY.top
                      }
                      className="fill-neutral-1 text-lg font-bold leading-5.25 @cred-md:text-xl @cred-md:leading-5.75 @cred-lg:text-[22px] @cred-lg:leading-6.5 @cred-xl:text-[30px] @cred-xl:leading-8.75"
                    >
                      {currDubMeterScore.score + '%'}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) + labelsY.bottom * 1.35
                          : (viewBox.cy || 0) + labelsY.bottom
                      }
                      className="fill-neutral-1 text-xs font-bold leading-3.5 @cred-md:text-sm @cred-md:leading-4 @cred-lg:text-base @cred-lg:leading-4.75 @cred-xl:text-xl @cred-xl:leading-5.75"
                    >
                      {currDubMeterScore.label}
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
};
